import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { recoverFromStaleBuildAssetError } from './utility/deploy/staleBuildRecovery';
import { RouteErrorElement } from './pages/shared/RouteErrorElement';
import { RouteHydrateFallback } from './pages/shared/RouteHydrateFallback';
import { loadOfflineTransactionSummary } from './offline/offlineRouteLoader';
import ManagementPermissionGuard from './shared/component/permission-guard/ManagementPermissionGuard';

const routeErrorElement = <RouteErrorElement />;
const routeHydrateFallbackElement = <RouteHydrateFallback />;

const withHydrateFallback = (routes: RouteObject[]): RouteObject[] =>
  routes.map(
    (route) =>
      ({
        ...route,
        hydrateFallbackElement: route.hydrateFallbackElement ?? routeHydrateFallbackElement,
        children: route.children ? withHydrateFallback(route.children) : undefined,
      }) as RouteObject
  );

const lazyComponent = (importer: () => Promise<{ default: ComponentType }>) => async () => {
  try {
    const { default: Component } = await importer();
    return { Component };
  } catch (error) {
    recoverFromStaleBuildAssetError(error, 'react_router.lazy');
    throw error;
  }
};

const routes: RouteObject[] = [
  {
    path: '/',
    errorElement: routeErrorElement,
    lazy: lazyComponent(() => import('./layouts/Root')),
    children: [
      { index: true, element: <Navigate to="/statistics" replace /> },
      {
        path: 'clients',
        children: [
          {
            index: true,
            lazy: lazyComponent(() => import('./pages/clients')),
            loader: () => ({ title: 'Clientes' }),
          },
          {
            path: 'new',
            lazy: lazyComponent(() => import('./pages/clients/NewClient')),
            loader: () => ({ title: 'Nuevo Cliente' }),
          },
          {
            path: ':id',
            lazy: lazyComponent(() => import('./pages/clients/ClientDetail')),
            loader: () => ({ title: 'Detalles Cliente' }),
          },
          {
            path: 'edit/:id',
            lazy: lazyComponent(() => import('./pages/clients/NewClient')),
            loader: () => ({ title: 'Editar Cliente' }),
          },
        ],
      },
      {
        path: 'statistics',
        lazy: lazyComponent(() => import('./pages/statistics')),
        loader: () => ({ title: 'Estadísticas' }),
      },
      {
        path: 'transactions',
        lazy: lazyComponent(() => import('./pages/transactions')),
        loader: () => ({ title: 'Venta' }),
      },
      {
        path: 'transactions/summary/:id/return',
        lazy: lazyComponent(() => import('./pages/returns')),
        loader: () => ({ title: 'Devolver' }),
      },
      {
        path: 'transactions/summary/:id/virtual-return',
        lazy: lazyComponent(() => import('./pages/returns')),
        loader: () => ({ title: 'Devolver', isVirtualReturn: true }),
      },
      {
        path: 'transactions/summary',
        lazy: lazyComponent(loadOfflineTransactionSummary),
        loader: () => ({ title: 'Resumen' }),
      },
      {
        path: 'transactions/summary/:id',
        lazy: lazyComponent(() => import('./pages/transactions/summary/SummarySale')),
        loader: () => ({ title: 'Detalle de transacción', isFromRecords: false }),
      },
      {
        path: 'profile',
        lazy: lazyComponent(() => import('./pages/profile')),
        loader: () => ({ title: 'Perfil' }),
      },
      {
        path: 'credipagos',
        children: [
          {
            index: true,
            lazy: lazyComponent(() => import('./pages/creditpayment')),
            loader: () => ({ title: 'Credipagos' }),
          },
          {
            path: 'new',
            lazy: lazyComponent(
              () => import('./pages/creditpayment/createNewCreditpayment/NewCredipago')
            ),
            loader: () => ({ title: 'Credipago' }),
          },
        ],
      },
      {
        path: 'records',
        lazy: lazyComponent(() => import('./pages/records')),
        loader: () => ({ title: 'Registros' }),
      },
      {
        path: 'reports/inventory',
        element: <Navigate to="/management/reports/kardex" replace />,
      },
      {
        path: 'records/:id',
        lazy: lazyComponent(() => import('./pages/transactions/summary/SummarySale')),
        loader: () => ({ title: 'Detalle de transacción', isFromRecords: true }),
      },
      {
        path: 'credipagos',
        lazy: lazyComponent(() => import('./pages/creditpayment')),
        loader: () => ({ title: 'Credipago' }),
      },
      {
        path: 'management',
        element: <ManagementPermissionGuard />,
        children: [
          {
            path: 'coupons',
            lazy: lazyComponent(() => import('./pages/management/coupons')),
            loader: () => ({ title: 'Cupones' }),
          },
          {
            path: 'coupons/new',
            lazy: lazyComponent(() => import('./pages/management/coupons/CouponDetail')),
            loader: () => ({ title: 'Crear cupón' }),
          },
          {
            path: 'coupons/:id',
            lazy: lazyComponent(() => import('./pages/management/coupons/CouponDetail')),
            loader: async () => {
              const { defaultData } = await import(
                './pages/management/coupons/constants/dataCouponDetail'
              );
              return { title: 'Editar cupón', data: defaultData };
            },
          },
          {
            path: 'stores',
            lazy: lazyComponent(() => import('./pages/management/stores')),
            loader: () => ({ title: 'Almacenes', isDealer: true }),
          },
          {
            path: 'stores/new',
            lazy: lazyComponent(() => import('./pages/management/stores/StoreDetail')),
            loader: () => ({ title: 'Crear almacén' }),
          },
          {
            path: 'stores/:id',
            lazy: lazyComponent(() => import('./pages/management/stores/StoreDetail')),
            loader: async () => {
              const { defaultDataStore } = await import(
                './pages/management/stores/constants/dataStoreDetail'
              );
              return { title: 'Editar almacén', data: defaultDataStore };
            },
          },
          {
            path: 'stores/dealer/:id',
            lazy: lazyComponent(
              () => import('./pages/management/stores/dealers/StoreDetailDealers')
            ),
            loader: () => ({ title: 'Editar Almacén del Distribuidor' }),
          },
          {
            path: 'stores/dealer/new',
            lazy: lazyComponent(
              () => import('./pages/management/stores/dealers/StoreDetailDealers')
            ),
            loader: () => ({ title: 'Crear Almacén del Distribuidor' }),
          },
          {
            path: 'stores/dealer/move',
            lazy: lazyComponent(() => import('./pages/management/stores/dealers/MoveProduct')),
            loader: () => ({ title: 'Mover' }),
          },
          {
            path: 'stores/dealer/:id/move',
            lazy: lazyComponent(() => import('./pages/management/stores/dealers/MoveProduct')),
            loader: () => ({ title: 'Mover' }),
          },
          {
            path: 'stores/dealer/movement/:id/:originId',
            lazy: lazyComponent(
              () => import('./pages/management/stores/dealers/movement/ReviewStoreMovement')
            ),
            loader: () => ({ title: 'Revisar Movimiento' }),
          },
          {
            path: 'stores/dealer/:id/add-product',
            lazy: lazyComponent(
              () => import('./pages/management/stores/dealers/product/AddProduct')
            ),
            loader: () => ({ title: 'Añadir Productos' }),
          },
          {
            path: 'stores/:id/view',
            lazy: lazyComponent(() => import('./pages/management/stores/dealers/view')),
            loader: async () => {
              const { defaultDataView } = await import(
                './pages/management/stores/constants/dataStoreDetail'
              );
              return { title: 'Ver', data: defaultDataView };
            },
          },
          {
            path: 'companies',
            lazy: lazyComponent(() => import('./pages/management/companies')),
            loader: () => ({ title: 'Sociedades' }),
          },
          {
            path: 'organizations',
            lazy: lazyComponent(() => import('./pages/management/organizations')),
            loader: () => ({ title: 'Organizaciones' }),
          },
          {
            path: 'organizations/new',
            lazy: lazyComponent(
              () => import('./pages/management/organizations/OrganizationDetail')
            ),
            loader: () => ({ title: 'Nueva organización' }),
          },
          {
            path: 'organizations/:id',
            lazy: lazyComponent(
              () => import('./pages/management/organizations/OrganizationDetail')
            ),
            loader: () => ({ title: 'Detalle de organización' }),
          },
          {
            path: 'companies/new',
            lazy: lazyComponent(() => import('./pages/management/companies/CompanyDetail')),
            loader: () => ({ title: 'Nueva Sociedad' }),
          },
          {
            path: 'companies/:id',
            lazy: lazyComponent(() => import('./pages/management/companies/CompanyDetail')),
            loader: async () => {
              const { defaultDataCompany } = await import(
                './pages/management/companies/constants/dataCompanyDetail'
              );
              return { title: 'Editar sociedad', data: defaultDataCompany };
            },
          },
          {
            path: 'admin',
            lazy: lazyComponent(() => import('./pages/menu/AdministrationMenu')),
            loader: () => ({ title: 'Administración' }),
          },
          {
            path: 'catalogs',
            lazy: lazyComponent(() => import('./pages/management/catalogs')),
            loader: () => ({ title: 'Catalogos' }),
          },
          {
            path: 'freight',
            lazy: lazyComponent(() => import('./pages/management/freight/FreightList')),
            loader: () => ({ title: 'Fletes' }),
          },
          {
            path: 'freight/new',
            lazy: lazyComponent(() => import('./pages/management/freight/FreightDetail')),
            loader: () => ({ title: 'Crear flete' }),
          },
          {
            path: 'freight/:id',
            lazy: lazyComponent(() => import('./pages/management/freight/FreightDetail')),
            loader: () => ({ title: 'Editar flete' }),
          },
          {
            path: 'point-sales',
            lazy: lazyComponent(() => import('./pages/management/point-sales')),
            loader: () => ({ title: 'Puntos de venta' }),
          },
          {
            path: 'point-sales/new',
            lazy: lazyComponent(() => import('./pages/management/point-sales/PointSaleDetail')),
            loader: () => ({ title: 'Crear punto de venta' }),
          },
          {
            path: 'point-sales/:id',
            lazy: lazyComponent(() => import('./pages/management/point-sales/PointSaleDetail')),
            loader: () => ({ title: 'Editar punto de venta' }),
          },
          {
            path: 'branches',
            lazy: lazyComponent(() => import('./pages/management/branches')),
            loader: () => ({ title: 'Sucursales' }),
          },
          {
            path: 'branches/new',
            lazy: lazyComponent(() => import('./pages/management/branches/BranchDetail')),
            loader: () => ({ title: 'Crear sucursal' }),
          },
          {
            path: 'branches/:id',
            lazy: lazyComponent(() => import('./pages/management/branches/BranchDetail')),
          },
          {
            path: 'discounts',
            lazy: lazyComponent(() => import('./pages/management/discounts')),
            loader: () => ({ title: 'Descuentos' }),
          },
          {
            path: 'discounts/new',
            lazy: lazyComponent(() => import('./pages/management/discounts/DiscountDetail')),
            loader: () => ({ title: 'Crear descuento' }),
          },
          {
            path: 'discounts/:id',
            lazy: lazyComponent(() => import('./pages/management/discounts/DiscountDetail')),
            loader: () => ({ title: 'Editar descuento', data: {} }),
          },
          {
            path: 'users',
            lazy: lazyComponent(() => import('./pages/management/users/Users')),
            loader: () => ({ title: 'Usuarios' }),
          },
          {
            path: 'users/new',
            lazy: lazyComponent(() => import('./pages/management/users/UserDetail')),
            loader: () => ({ title: 'Crear usuario' }),
          },
          {
            path: 'users/:id',
            lazy: lazyComponent(() => import('./pages/management/users/UserDetail')),
            loader: () => ({ title: 'Editar usuario', data: {} }),
          },
          {
            path: 'users/roles/new',
            lazy: lazyComponent(() => import('./pages/management/users/roles/RoleDetail')),
            loader: () => ({ title: 'Crear rol' }),
          },
          {
            path: 'users/roles/:id',
            lazy: lazyComponent(() => import('./pages/management/users/roles/RoleDetail')),
            loader: () => ({ title: 'Editar rol' }),
          },
          {
            path: 'catalogs/:id',
            lazy: lazyComponent(() => import('./pages/management/catalogs/CatalogDetail')),
            loader: () => ({ title: 'Revisar producto' }),
          },
          {
            path: 'catalogs/new',
            lazy: lazyComponent(() => import('./pages/management/catalogs/CatalogDetail')),
            loader: () => ({ title: 'Crear producto' }),
          },
          {
            path: 'cash-closing',
            lazy: lazyComponent(() => import('./pages/management/cash-closing')),
            loader: () => ({ title: 'Cierre de caja' }),
          },
          {
            path: 'cash-closing/:id/view',
            lazy: lazyComponent(() => import('./pages/management/cash-closing/CashClosingDetail')),
            loader: () => ({ title: 'Nuevo cierre de caja' }),
          },
          {
            path: 'cash-closing/:id/summary',
            lazy: lazyComponent(() => import('./pages/management/cash-closing/CashClosingSummary')),
            loader: () => ({ title: 'Detalle cierre de caja' }),
          },
          {
            path: 'promotions',
            lazy: lazyComponent(() => import('./pages/management/promotions')),
            loader: () => ({ title: 'Promociones' }),
          },
          {
            path: 'promotions/detail',
            lazy: lazyComponent(() => import('./pages/management/promotions/PromotionDetail')),
            loader: () => ({ title: 'Crear promoción' }),
          },
          {
            path: 'promotions/detail/:id',
            lazy: lazyComponent(() => import('./pages/management/promotions/PromotionDetail')),
            loader: () => ({ title: 'Editar promoción' }),
          },
          {
            path: 'zones',
            lazy: lazyComponent(() => import('./pages/management/zones/ZonesList')),
            loader: () => ({ title: 'Zonas' }),
          },
          {
            path: 'zones/new',
            lazy: lazyComponent(() => import('./pages/management/zones/ZoneDetail')),
            loader: () => ({ title: 'Crear zona' }),
          },
          {
            path: 'zones/:id',
            lazy: lazyComponent(() => import('./pages/management/zones/ZoneDetail')),
            loader: () => ({ title: 'Editar zona' }),
          },
          {
            path: 'prices',
            lazy: lazyComponent(() => import('./pages/management/prices/PricesPage')),
            loader: () => ({ title: 'Precios' }),
          },
          {
            path: 'reports/kardex',
            lazy: lazyComponent(() => import('./pages/reports/inventory')),
            loader: () => ({ title: 'Reporte Kardex' }),
          },
          {
            path: 'prices/new',
            lazy: lazyComponent(() => import('./pages/management/prices/new/NewPricePage')),
            loader: () => ({ title: 'Nuevo precio' }),
          },
          {
            path: 'prices/zones/new',
            lazy: lazyComponent(() => import('./pages/management/prices/new/NewZonePage')),
            loader: () => ({ title: 'Nueva zona' }),
          },
          {
            path: 'prices/zones/:id',
            lazy: lazyComponent(() => import('./pages/management/prices/new/NewZonePage')),
            loader: () => ({ title: 'Editar zona' }),
          },
          {
            path: 'prices/:id',
            lazy: lazyComponent(() => import('./pages/management/prices/new/NewPricePage')),
            loader: () => ({ title: 'Editar precio' }),
          },
        ],
      },
      {
        path: 'access-denied',
        lazy: lazyComponent(() => import('./shared/component/access-denied/AccessDenied')),
      },
    ],
  },

  {
    path: 'receipt-search',
    errorElement: routeErrorElement,
    lazy: lazyComponent(() => import('./pages/receipt-search')),
  },
  {
    path: 'login',
    errorElement: routeErrorElement,
    lazy: lazyComponent(() => import('./components/login/Login')),
  },
  {
    path: 'forgotPassword',
    errorElement: routeErrorElement,
    lazy: lazyComponent(() => import('./components/login/ForgotPasswordStepOne')),
  },
  {
    path: 'reset-password',
    errorElement: routeErrorElement,
    lazy: lazyComponent(() => import('./components/login/ForgotPasswordStepTwo')),
  },
];

export const router = createBrowserRouter(withHydrateFallback(routes));
